/**
 * The Selected Work carousel's content.
 *
 * ── Read this before adding anything ──────────────────────────────────────
 *
 * Every entry here is a claim a prospect can check. Nothing invented goes in:
 * no composite clients, no "a leading retailer", no numbers that are not
 * measured. A consulting buyer who catches one fabricated case study discounts
 * everything else on the page, and they do check.
 *
 * Empty this array and the section removes itself from the homepage — the same
 * rule TestimonialsSection follows. An absent section costs less than a section
 * full of placeholders.
 *
 * ── Adding a project ──────────────────────────────────────────────────────
 *
 * 1. Put the images in /public/assets/work/.
 *    - `desktopImages`  landscape shots, ~1440px wide. The first opens the frame.
 *    - `mobileImages`   portrait shots, ~390px wide.
 *    - `desktopVideo` / `mobileVideo` optional, and never a replacement for
 *      the stills: the first still is the poster frame the video needs.
 * 2. Add an entry below.
 * 3. `tags` are the tools or disciplines actually used. They are recorded and
 *    editable in the dashboard but no longer shown on the home page — the
 *    chips they used to render as were the tallest thing in the band, so the
 *    whole row was sized by a list of framework names. Keep them to one or two
 *    words anyway, for whatever shows them next.
 */

export type WorkItem = {
  id: string;
  /** Lower-case on purpose: it sits under uppercase section headings, and the
   *  case difference is what carries the hierarchy without a size change. */
  name: string;
  /** One line, shown as the mono eyebrow above the description. */
  kind: string;
  /** Two short paragraphs at most. The column is ~46 characters wide. */
  description: string[];
  tags: string[];
  /** Landscape views, in order. The first is the opening frame. */
  desktopImages: string[];
  /** Portrait views, in order. */
  mobileImages: string[];
  /** Optional. Plays in place of the first landscape view, which becomes its
   *  poster frame — so an autoplay-blocked browser still shows something. */
  desktopVideo?: string;
  /** Optional. Same relationship to the first portrait view. */
  mobileVideo?: string;
  /** Omit when there is nothing public to link to. */
  href?: string;
};

export const WORK: WorkItem[] = [
  {
    id: 'noventilabs',
    name: 'noventi labs',
    kind: 'Marketing site · In-house',
    description: [
      'Our own site, rebuilt as a diagnostic instrument rather than a brochure: a visitor picks the problem that sounds like theirs and the page routes them to the service that addresses it.',
      'Next.js and TypeScript, a monochrome editorial design system on a single 1280px grid, generative canvas graphics instead of stock photography, and a five-minute self-assessment feeding a booking flow.',
    ],
    tags: ['Next.js', 'TypeScript', 'Tailwind', 'Supabase', 'Canvas'],
    desktopImages: [
      '/assets/work/noventi-desktop.png',
      '/assets/work/thumb-services.png',
      '/assets/work/thumb-process.png',
      '/assets/work/thumb-why.png',
      '/assets/work/thumb-insights.png',
    ],
    mobileImages: ['/assets/work/noventi-mobile.png'],
    href: '/',
  },
];
